import { get, put, postJson, deleteRequest } from './apiClient';

/**
 * Assorted-item groups — see ASSORTED_ITEM_GROUPING_PLAN.md.
 *
 * A vendor invoices USC per flavour ("YOGHURT STRAWBERRY 125G"); the client
 * orders "Assorted Yoghurt". A group owns the client-facing name plus the
 * policies that decide what the collapsed line costs and whether it carries VAT.
 */

export type PriceStrategy = 'highest' | 'latest' | 'weighted_avg' | 'manual';
export type VatPolicy = 'any_member' | 'all_members' | 'force_on' | 'force_off';
export type AllocationStrategy = 'fefo' | 'oldest_purchase' | 'manual';
export type MatchRuleKind = 'keyword' | 'regex' | 'exact';

export interface MatchRule {
  kind: MatchRuleKind;
  value: string;
  /** Scope the rule to one vendor — "only LJ Williams bills yoghurt this way". */
  vendorId?: string | null;
}

/** Units a group's canonical size may be expressed in. Volume never converts into mass. */
export type SizeUnit = 'kg' | 'g' | 'lb' | 'oz' | 'l' | 'ml';

export interface ItemGroupUom {
  /** What one group unit is called — PACK, CASE, EACH. */
  level1Name: string;
  level2Name?: string;
  /** The unit `weightPerLevel1` is measured in. */
  weightUnit?: SizeUnit | null;
  /** The size of ONE level1 unit, e.g. 125 with weightUnit 'g'. */
  weightPerLevel1?: number | null;
}

export interface ItemGroup {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  isActive: boolean;
  category?: string | null;
  sub_category?: string | null;
  uom: ItemGroupUom;
  priceStrategy: PriceStrategy;
  manualPrice?: number | null;
  vatPolicy: VatPolicy;
  allocationStrategy: AllocationStrategy;
  matchRules: MatchRule[];
  excludePatterns: string[];
  memberCount?: number;
  inventoryMemberCount?: number;
  suggestionCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ItemGroupMember {
  _id: string;
  item_name: string;
  supplier?: string | null;
  currency?: string | null;
  item_price?: number | null;
  vat_applicable?: boolean | null;
  last_updated?: string;
  uomLevel1Name?: string;
  unitSize?: string;
  /** How many group units one member unit is worth (plan §6-P1). */
  factor: number;
  /** 'same_unit' | 'size:uom' | 'size:unit_size' | 'size:name' | 'assumed' | 'manual'. */
  factorSource?: string;
  /** The derivation in words, e.g. "1 CASE (3 kg) = 24 × PACK (125 g)". */
  factorNote?: string;
  uomWarning?: string;
  method?: string;
  locked?: boolean;
  priceInGroupUnits?: number | null;
  setsGroupPrice?: boolean;
}

export interface ItemGroupInventoryMember {
  _id: string;
  name: string;
  uomLevel1Name?: string;
  factor: number;
  factorSource?: string;
  factorNote?: string;
  uomWarning?: string;
  level1Qty: number;
  groupUnits: number;
  expiryDate?: string | null;
  lastSupplierName?: string;
  costPerLevel1?: number | null;
}

export interface ItemGroupDetail extends ItemGroup {
  /** True when the member list is one page of a larger group. The rollup below is not — it covers every member. */
  truncated?: boolean;
  /** How many members carry an unresolved unit warning. */
  warningCount?: number;
  memberPage?: number;
  memberPageSize?: number;
  rollup: {
    price: number | null;
    priceSourceMemberId: string | null;
    priceSpread: { min: number; max: number } | null;
    vat: boolean;
    vatMixed: boolean;
    stockLevel1: number;
    uomLevel1Name: string;
  };
  members: ItemGroupMember[];
  inventoryMembers: ItemGroupInventoryMember[];
}

/** One of several groups answering to the same display name. Only the slug is unique. */
export interface SameNameGroup {
  _id: string;
  slug: string;
  isActive: boolean;
  memberCount: number;
  ruleCount: number;
  /** True for the group this suggestion actually targets. */
  isTarget: boolean;
}

export interface ItemGroupSuggestion {
  _id: string;
  item_name: string;
  supplier?: string | null;
  item_price?: number | null;
  unit?: string | null;
  last_updated?: string;
  suggestedGroupId: string;
  suggestedGroupName: string;
  /** Unique where the name is not — what tells two identically-named groups apart. */
  suggestedGroupSlug?: string;
  suggestedGroupMemberCount?: number | null;
  suggestedGroupRules?: string[];
  /** Another group carries this exact name, so the name alone doesn't say where the item is going. */
  suggestedGroupIsAmbiguous?: boolean;
  /** Every group sharing the name, so the screen can spell out which one wins. */
  sameNameGroups?: SameNameGroup[];
  confidence: number | null;
}

export type ItemGroupPayload = Partial<
  Pick<
    ItemGroup,
    | 'name'
    | 'description'
    | 'isActive'
    | 'category'
    | 'sub_category'
    | 'uom'
    | 'priceStrategy'
    | 'manualPrice'
    | 'vatPolicy'
    | 'allocationStrategy'
    | 'matchRules'
    | 'excludePatterns'
  >
>;

export async function listItemGroups(params?: {
  search?: string;
  includeInactive?: boolean;
}): Promise<{ success: boolean; data: ItemGroup[] }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.includeInactive) qs.set('includeInactive', 'true');
  const query = qs.toString();
  return get(`/item-groups${query ? `?${query}` : ''}`);
}

export async function getItemGroup(
  id: string,
  params?: { memberPage?: number; memberLimit?: number }
): Promise<{ success: boolean; data: ItemGroupDetail }> {
  const qs = new URLSearchParams();
  if (params?.memberPage) qs.set('memberPage', String(params.memberPage));
  if (params?.memberLimit) qs.set('memberLimit', String(params.memberLimit));
  const query = qs.toString();
  return get(`/item-groups/${id}${query ? `?${query}` : ''}`);
}

/** Override one member's conversion, or pass null to hand it back to the derived value. */
export async function setItemGroupMemberFactor(
  groupId: string,
  memberId: string,
  factor: number | null
): Promise<{
  success: boolean;
  data: { factor: number; source: string; derived: number };
  message: string;
}> {
  return put(`/item-groups/${groupId}/members/${memberId}/factor`, { factor });
}

/** Re-derive every member's factor from the group's current unit and size. */
export async function recomputeItemGroupFactors(
  groupId: string
): Promise<{ success: boolean; data: { updated: number; warnings: number }; message: string }> {
  return postJson(`/item-groups/${groupId}/recompute-factors`, {});
}

export async function createItemGroup(
  payload: ItemGroupPayload
): Promise<{ success: boolean; data: ItemGroup; message: string; warning?: string | null }> {
  return postJson('/item-groups', payload);
}

export async function updateItemGroup(
  id: string,
  payload: ItemGroupPayload
): Promise<{ success: boolean; data: ItemGroup; message: string; warning?: string | null }> {
  return put(`/item-groups/${id}`, payload);
}

export async function deleteItemGroup(id: string): Promise<{ success: boolean; message: string }> {
  return deleteRequest(`/item-groups/${id}`);
}

export async function addItemGroupMembers(
  id: string,
  itemIds: string[],
  factor?: number
): Promise<{
  success: boolean;
  data: { assigned: number; inventoryAssigned: number; warnings: Array<{ id: string; name: string; warning: string }> };
  message: string;
}> {
  return postJson(`/item-groups/${id}/members`, { itemIds, factor });
}

export async function removeItemGroupMembers(
  id: string,
  itemIds: string[]
): Promise<{ success: boolean; data: { removed: number; inventoryRemoved: number }; message: string }> {
  return postJson(`/item-groups/${id}/members/remove`, { itemIds });
}

export async function listItemGroupSuggestions(
  limit = 200,
  page = 1
): Promise<{
  success: boolean;
  data: ItemGroupSuggestion[];
  total: number;
  /** Of `total`, how many target a group whose name is shared with another — queue-wide, not this page. */
  ambiguousTotal: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}> {
  return get(`/item-groups/suggestions?limit=${limit}&page=${page}`);
}

/**
 * `all: 'accept' | 'reject'` resolves every pending suggestion server-side
 * instead of the ids currently on screen — the only practical way to clear a
 * queue of thousands.
 */
export async function resolveItemGroupSuggestions(payload: {
  accept?: string[];
  reject?: string[];
  all?: 'accept' | 'reject';
  groupId?: string;
}): Promise<{
  success: boolean;
  data: { accepted: number; inventoryAccepted: number; rejected: number };
  message: string;
}> {
  return postJson('/item-groups/suggestions/resolve', payload);
}

/**
 * Re-run match rules over rows already ingested before the group existed.
 * Scans the whole catalogue and the whole stock list unless `limit` says otherwise.
 */
export async function resyncItemGroupRules(payload?: {
  suggest?: boolean;
  /** Rows to scan. Omit for the whole catalogue, which is the default. */
  limit?: number;
  /** How many unmatched rows the AI pass may look at. Rules always see everything. */
  suggestLimit?: number;
}): Promise<{
  success: boolean;
  data: {
    assigned: number;
    suggested: number;
    suggestionsSkipped: number;
    askable: number;
    locked: number;
    unmatched: number;
    unchanged: number;
    scanned: number;
    inventoryAssigned: number;
    inventoryScanned: number;
    inventoryMirrored: number;
    ambiguousNames: number;
  };
  message: string;
}> {
  return postJson('/item-groups/resync', payload || {});
}

export interface RulePreviewMatch {
  _id: string;
  item_name: string;
  supplier?: string | null;
  item_price?: number | null;
  unit?: string | null;
  /** Catalogue rows carrying this name — one per invoice line it ever appeared on. */
  rowCount: number;
  alreadyGrouped: boolean;
  locked: boolean;
}

/** Dry run: which catalogue rows would these rules sweep in? */
export async function previewItemGroupRules(payload: {
  matchRules: MatchRule[];
  excludePatterns?: string[];
  limit?: number;
}): Promise<{
  success: boolean;
  data: {
    /** Distinct products, most-invoiced first, capped at `limit` for display. */
    matches: RulePreviewMatch[];
    /** Every catalogue ROW that would join — counted across the whole catalogue. */
    total: number;
    /** How many distinct products that is. */
    distinct: number;
    /** True when there are more distinct products than the display cap. */
    truncated: boolean;
    scanned: number;
    alreadyGrouped: number;
    locked: number;
    /** Products a rule caught but an exclude pattern threw back out. */
    vetoed: number;
    vetoedRows: number;
    vetoedNames: string[];
    vendorScopedRules: number;
  };
}> {
  return postJson('/item-groups/preview-rules', payload);
}
